<template>
  <div class="w-full py-16 bg-vp-base-100 p-8">
    <div
      v-if="instructionsSent"
      class="mx-auto w-full sm:w-10/12 md:w-8/12 lg:w-4/12 xl:3/12 2xl:w-2/12"
    >
      <div>{{ $gettext('If the email entered exists in our database, you will receive password reset instructions shortly.') }}</div>
      <router-link
        :to="{ name: 'Login' }"
        class="hover:no-underline flex items-center justify-center w-full bg-vp-primary-900 rounded-lg mt-4"
      >
        <div class="text-vp-base-100 text-sm font-semibold p-2">
          {{ $gettext('Return to login') }}
        </div>
      </router-link>
    </div>
    <div
      v-else
      class="mx-auto w-full sm:w-10/12 md:w-8/12 lg:w-4/12 xl:3/12 2xl:w-2/12"
    >
      <h1 class="text-2xl my-8 text-center">
        {{ $gettext('Forgot Password') }}
      </h1>
      <Form
        :form-id="formId"
        @submit="onSubmit"
      >
        <VPInput
          input-id="email"
          :label="$gettext('Email')"
          :placeholder="$gettext('Enter your email address')"
          autocomplete="username"
          autocapitalize="off"
          autocorrect="off"
          :disabled="loading"
        />
        <VPButton
          type="submit"
          class="my-4 block w-full disabled:cursor-not-allowed"
          :disabled="loading"
          @submit="onSubmit"
        >
          {{ $gettext('Send instructions') }}
        </VPButton>
      </Form>
    </div>
  </div>
</template>

<script setup lang='ts'>
import { ref } from 'vue';
import { Form, useFormsStoreComposable } from 'vuniform';
import VPButton from '@/components/form/VPButton.vue';
import VPInput from '@/components/form/VPInput.vue';
import { Auth } from '@vp/common';
import { useGettext } from 'vue3-gettext';
import { useToast } from 'vue-toastification';
import { useConnectionStore } from '@/stores/connection';
import * as Sentry from '@sentry/vue';

const connectionStore = useConnectionStore();
const apiClient = connectionStore.apiClient!;
const loading = ref<boolean>(false);
const showConfirmation = ref<boolean>(false);
const toast = useToast();
const { $gettext } = useGettext();

const instructionsSent = ref(false);

const { formId, formGetValues, formsStore } = useFormsStoreComposable('forgot-password-form');

const onSubmit = async () => {
  loading.value = true;
  showConfirmation.value = false;
  formsStore.CLEAR_ERRORS(formId);

  const { email } = formGetValues<{ email: string }>();

  try {
    const { success, errors } = await Auth.forgotPassword(apiClient, email);

    if (success) {
      formsStore.RESET_FIELD_VALUES(formId);
      instructionsSent.value = true;
      return;
    }
    for (const error of errors!) {
      toast.error(error);
    }
  } catch (e) {
    Sentry.captureException(e, {
      extra: {
        context: 'Forgot password error',
      },
    });
    toast.error($gettext('Something went wrong. Please try again or contact support'));
  } finally {
    loading.value = false;
  }
};

</script>
